import { EyeOutlined, HistoryOutlined, UndoOutlined } from '@ant-design/icons';
import { App, Button, Empty, Popconfirm, Spin, Tooltip } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { type WorkflowTemplate, workflowsApi } from '@/api/workflows';
import { useRoles } from '@/auth/useRoles';
import { ICON_BTN_PROPS as iconBtn } from '@/components/iconButton';
import { PageHeader } from '@/components/PageHeader';
import { WorkflowCard } from '@/components/WorkflowCard';
import { useFetchedList } from '@/hooks/useFetchedList';
import { OUTER_WRAP } from '@/styles/workflowCardStyles';

/**
 * Archived workflows — retired by an approver instead of deleted.
 *
 * Reached from the header profile menu, the same route pattern as Audit
 * History (`/<role>/archive`). BOTH roles can view it, per the ticket ("The
 * Originator should also be able to see archived workflows"), but only the
 * Approver gets the Restore action — the same asymmetry the ticket describes
 * for archiving.
 *
 * A card here keeps the Approved/Rejected status it held when it was archived;
 * archiving is orthogonal to status, so the tag still tells you what state the
 * workflow was retired from.
 */
export function ArchivePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { role } = useRoles();
  const { message } = App.useApp();
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const {
    data: items,
    loading,
    setData,
  } = useFetchedList(() => workflowsApi.listArchived(), t, 'workflow.archive.loadFailed');

  const isApprover = role === 'Approver';
  const dashboardRoute = isApprover ? '/approver/dashboard' : '/originator/dashboard';
  const routeBase = isApprover ? 'approver' : 'originator';

  const restore = async (w: WorkflowTemplate) => {
    setRestoringId(w.id);
    try {
      await workflowsApi.unarchive(w.id);
      // Restoring lands the workflow in DRAFT — say so, because the card
      // disappears from this page and reappears on the dashboard under a
      // different status than the one shown here.
      message.success(
        t('workflow.archive.restored', {
          name: w.nameEn,
          defaultValue: '"{{name}}" was restored as a draft.',
        }),
      );
      // Drop the restored card locally rather than refetching — the BE has
      // already removed it from the archived list, and the optimistic removal
      // keeps the page from flashing an empty grid mid-refetch.
      setData((prev) => (prev ?? []).filter((x) => x.id !== w.id));
    } catch {
      message.error(
        t('workflow.archive.restoreFailed', { defaultValue: 'Could not restore the workflow.' }),
      );
    } finally {
      setRestoringId(null);
    }
  };

  const grid: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: 16,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <PageHeader
        title={t('workflow.archive.title', { defaultValue: 'Archived Workflows' })}
        onBack={() => navigate(dashboardRoute)}
      />
      {/* ONE container. The Audit History shell nests a CARD inside
          OUTER_WRAP, which reads as a double border here because the grid
          below already draws a border per workflow card. The dashboard uses a
          single container, so this matches it. */}
      <div style={OUTER_WRAP}>
        {loading && items === null ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spin />
          </div>
        ) : (items ?? []).length === 0 ? (
          <Empty
            description={t('workflow.archive.empty', {
              defaultValue: 'No archived workflows.',
            })}
          />
        ) : (
          <div style={grid}>
            {(items ?? []).map((w) => (
              <WorkflowCard
                key={w.id}
                workflow={w}
                // The status it was archived FROM, shown as-is. This used to
                // collapse to `Approved : Rejected`, which mislabelled every
                // other status as "Rejected" — and since 2026-08-01 a published
                // workflow with a new version in flight archives as Draft.
                versionLabel={w.currentVersionLabel || w.inProgressVersionLabel || 'v1'}
                status={w.status}
                actions={
                  <>
                    <Tooltip title={t('workflow.builder.preview')}>
                      <Button
                        {...iconBtn}
                        aria-label={t('workflow.builder.preview')}
                        icon={<EyeOutlined />}
                        onClick={() => navigate(`/${routeBase}/workflows/${w.id}/preview`)}
                      />
                    </Tooltip>
                    <Tooltip title={t('workflow.list.versions')}>
                      <Button
                        {...iconBtn}
                        aria-label={t('workflow.list.versions')}
                        icon={<HistoryOutlined />}
                        onClick={() => navigate(`/${routeBase}/workflows/${w.id}/versions`)}
                      />
                    </Tooltip>
                    {isApprover && (
                      <Popconfirm
                        title={t('workflow.archive.restoreTitle', {
                          defaultValue: 'Restore this workflow?',
                        })}
                        // Spelled out because the status changes: it does
                        // NOT go back to Approved/Rejected.
                        description={t('workflow.archive.restoreConfirm', {
                          defaultValue:
                            'It will return to the dashboard as a draft, not to its previous status.',
                        })}
                        okText={t('workflow.archive.restore', { defaultValue: 'Restore' })}
                        cancelText={t('common.cancel', { defaultValue: 'Cancel' })}
                        onConfirm={() => restore(w)}
                      >
                        <Tooltip title={t('workflow.archive.restore', { defaultValue: 'Restore' })}>
                          <Button
                            {...iconBtn}
                            aria-label={t('workflow.archive.restore', { defaultValue: 'Restore' })}
                            icon={<UndoOutlined />}
                            loading={restoringId === w.id}
                          />
                        </Tooltip>
                      </Popconfirm>
                    )}
                  </>
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
